/**
 * Client Nutrition Page - Daily macro logging with progress bars (Epic 5 MVP+)
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Apple, Flame, Beef, Wheat, Droplets, Plus, Loader2,
} from "lucide-react";
import { useMyNutrition, logNutrition } from "../hooks/useNutrition";
import { useMyNutritionTargets } from "../hooks/useMessaging";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Toaster, toast } from "sonner";

const containerV = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.08 } } };
const itemV = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

const MACROS = [
  { key: "calories_kcal", targetKey: "target_calories", label: "Calories", unit: "kcal", icon: Flame, color: "text-orange-400", barColor: "bg-orange-400", barBg: "bg-orange-400/10" },
  { key: "protein_g", targetKey: "target_protein", label: "Protéines", unit: "g", icon: Beef, color: "text-red-400", barColor: "bg-red-400", barBg: "bg-red-400/10" },
  { key: "carbs_g", targetKey: "target_carbs", label: "Glucides", unit: "g", icon: Wheat, color: "text-amber-400", barColor: "bg-amber-400", barBg: "bg-amber-400/10" },
  { key: "fat_g", targetKey: "target_fats", label: "Lipides", unit: "g", icon: Droplets, color: "text-sky-400", barColor: "bg-sky-400", barBg: "bg-sky-400/10" },
];

function ProgressBar({ value, target, barColor }) {
  if (!target || target === 0) return null;
  const pct = Math.min((value / target) * 100, 120);
  const isOver = pct > 105;
  const bgColor = isOver ? "bg-red-500" : barColor;

  return (
    <div className="mt-2">
      <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${bgColor}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <div className="flex justify-between mt-1">
        <span className={`text-xs font-bold ${isOver ? "text-red-400" : pct >= 90 ? "text-emerald-400" : "text-zinc-500"}`}>
          {Math.round(pct)}%
        </span>
        <span className="text-xs text-zinc-600">Objectif: {target}</span>
      </div>
    </div>
  );
}

export default function NutritionPage() {
  const navigate = useNavigate();
  const { logs, mutate } = useMyNutrition();
  const { targets } = useMyNutritionTargets();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    log_date: new Date().toISOString().split("T")[0],
    calories_kcal: "", protein_g: "", carbs_g: "", fat_g: "", notes: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await logNutrition({
        log_date: form.log_date,
        calories_kcal: parseInt(form.calories_kcal) || 0,
        protein_g: parseFloat(form.protein_g) || 0,
        carbs_g: parseFloat(form.carbs_g) || 0,
        fat_g: parseFloat(form.fat_g) || 0,
        notes: form.notes || null,
      });
      toast.success("Nutrition enregistrée");
      setDialogOpen(false);
      setForm({ log_date: new Date().toISOString().split("T")[0], calories_kcal: "", protein_g: "", carbs_g: "", fat_g: "", notes: "" });
      mutate();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const todayLog = logs.find((l) => l.log_date === new Date().toISOString().split("T")[0]);

  return (
    <div className="min-h-screen bg-zinc-950 pb-20">
      <Toaster position="top-right" richColors />
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-lg mx-auto px-4 flex items-center justify-between h-14">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard" className="text-zinc-400 hover:text-white -ml-2">
            <ArrowLeft className="w-5 h-5 mr-1" /> Retour
          </Button>
          <div className="flex items-center gap-2">
            <Apple className="w-5 h-5 text-orange-400" />
            <span className="font-heading font-bold text-white">NUTRITION</span>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        <motion.div variants={containerV} initial="hidden" animate="show" className="space-y-6">
          <motion.div variants={itemV}>
            <Button onClick={() => setDialogOpen(true)} data-testid="add-nutrition-button" className="w-full h-12 bg-orange-400 hover:bg-orange-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full">
              <Plus className="w-5 h-5 mr-2" /> Saisir ma journée
            </Button>
          </motion.div>

          {/* Today summary with progress bars */}
          {todayLog && (
            <motion.div variants={itemV}>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base text-white flex items-center gap-2">
                    <Flame className="w-4 h-4 text-orange-400" /> Aujourd'hui
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {MACROS.map(({ key, targetKey, label, unit, icon: Icon, color, barColor, barBg }) => {
                    const val = todayLog[key];
                    const target = targets?.[targetKey];
                    return (
                      <div key={key} className={`p-3 rounded-xl ${barBg}`} data-testid={`today-${key}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${color}`} />
                            <span className="text-sm text-zinc-300">{label}</span>
                          </div>
                          <span className="text-lg font-bold text-white">
                            {val} <span className="text-xs text-zinc-500">{unit}</span>
                            {target != null && (
                              <span className="text-xs text-zinc-600"> / {target}</span>
                            )}
                          </span>
                        </div>
                        <ProgressBar value={val} target={target} barColor={barColor} />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* No targets info */}
          {!targets && todayLog && (
            <motion.div variants={itemV}>
              <p className="text-xs text-zinc-600 text-center italic">Votre coach n'a pas encore défini d'objectifs nutritionnels</p>
            </motion.div>
          )}

          {/* History */}
          <motion.div variants={itemV}>
            <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">Historique</h3>
            {logs.length > 0 ? (
              <div className="space-y-2">
                {logs.map((log) => (
                  <Card key={log.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`nutrition-${log.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-sm font-semibold text-white">
                          {new Date(log.log_date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
                        </p>
                        <Badge className="bg-orange-400/10 text-orange-400 border-orange-400/30 text-xs">{log.calories_kcal} kcal</Badge>
                      </div>
                      <div className="flex gap-3 text-xs text-zinc-500">
                        <span className="text-red-400">{log.protein_g}g P</span>
                        <span className="text-amber-400">{log.carbs_g}g C</span>
                        <span className="text-sky-400">{log.fat_g}g F</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-12 text-center">
                  <Apple className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">Aucun log nutrition</p>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </motion.div>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Saisie Nutrition</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-zinc-300">Date</Label>
              <Input type="date" value={form.log_date} onChange={(e) => setForm({ ...form, log_date: e.target.value })} required data-testid="nutrition-date" className="bg-zinc-950 border-zinc-800 h-11" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {MACROS.map(({ key, label, unit, icon: Icon, color }) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs flex items-center gap-1"><Icon className={`w-3 h-3 ${color}`} />{label} ({unit})</Label>
                  <Input type="number" step={key === "calories_kcal" ? "1" : "0.1"} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder="0" data-testid={`nutrition-${key}`} className="bg-zinc-950 border-zinc-800 h-10" />
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Notes (optionnel)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ex: Jour de repos" data-testid="nutrition-notes" className="bg-zinc-950 border-zinc-800 h-11" />
            </div>
            <Button type="submit" disabled={saving} data-testid="submit-nutrition" className="w-full h-12 bg-orange-400 hover:bg-orange-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enregistrer"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
